import { type Product } from '@/features/shop/services/Product/types'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { type OnProductArgs } from '@/features/shop/components/ProductCard/types'
import { RetailerShelfHeading } from '@/features/shop/components/GlobalSearchPage/RetailerShelfHeading'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useCallback } from 'react'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import {
  type RichTextElement,
  type Link,
  type CMSProductShelfInnerData,
} from '@/features/cms/components/CMS/types'
import { Shelf } from '@/features/shop/components/Shelf'
import { useDeliveryStoreById } from '@/features/shop/services/ShoppingStore/hooks'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { MIN_HEIGHT } from '@shared/constants/productCard'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'
import { getRetailerBaseUrl } from '@shared/constants/routes'
import { getShelfViewAll } from '@/features/cms/components/CMS/components/Shelves/getShelfViewAll'

type Props = {
  id: string
  storeId: number | null
  src: string | undefined
  heading: string
  index: number
  products: Product[]
  subheading: RichTextElement[] | undefined
  viewAllLink: Link | null | undefined
  data: CMSProductShelfInnerData
  analyticSlug?: string
  adPlacementId?: string
  linkToUniversalPDP: boolean
}

export const GlobalProductShelf = ({
  id,
  storeId,
  heading,
  index,
  src,
  products,
  subheading,
  viewAllLink,
  data,
  analyticSlug,
  adPlacementId,
  linkToUniversalPDP,
}: Props) => {
  const retailer = useDeliveryStoreById(storeId)
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { selectStore } = useGlobalHomeSelectStore()
  const normalizedViewAllLinkUrl = getRetailerBaseUrl(retailer?.name, viewAllLink?.url)
  const viewAll = useCallback(() => {
    trackCMSElementClicked({
      type: 'link',
      content: 'View all',
      // For the shelf-related CMS components, the DS team would like for us to continue sending the shelf's heading/title.
      shelf_name: heading,
      shelf_index: index,
      store_id: retailer?.id,
    })
    if (retailer) {
      return selectStore(retailer, normalizedViewAllLinkUrl)
    }
  }, [heading, index, retailer, selectStore, trackCMSElementClicked, normalizedViewAllLinkUrl])

  const shelfViewAll = getShelfViewAll({
    viewAllHref: normalizedViewAllLinkUrl,
    onViewAll: viewAll,
  })

  const handleProductClick = useCallback(
    async (args: OnProductArgs, e: React.MouseEvent) => {
      // Prevent the default behavior of the product card link since we are handling the navigation ourselves
      e.preventDefault()
      if (retailer) {
        return selectStore(
          retailer,
          getProductPageUrl({
            id: args.product.id,
            universal_id: args.product.universal_id,
          }),
        )
      }
    },
    [selectStore, retailer],
  )

  const shelfProducts = useRenderShelfProducts({
    shelfName: heading,
    shelfIndex: index,
    viewAll: shelfViewAll,
    products,
    ...(retailer && { handleOnProductClick: handleProductClick }),
    trackingProps: { analyticSlug, adPlacementId },
    linkToUniversalPDP,
  })
  if (!retailer) return null

  return (
    <RetailerShelfLayout id={id}>
      <RetailerShelfHeading
        store={retailer}
        index={index}
        handleStoreClick={shelfViewAll.onViewAll}
        viewAllHref={shelfViewAll.viewAllHref}
        showPricing={false}
        customHeading={heading}
        subheading={subheading}
        interactiveImage={false}
        imageSize={56}
        src={src}
        data={data}
        astroDlsTheme
      />

      <Shelf
        hideTitle
        title={heading}
        shelfIndex={index}
        minHeight={MIN_HEIGHT}
        store_id={retailer.id}
        slideSpacing={16}
      >
        {shelfProducts}
      </Shelf>
    </RetailerShelfLayout>
  )
}

const RetailerShelfLayout = styled(GridLayoutContainer)`
  @media ${screenSizes.max_mobile} {
    --padding-inline: 0px;
  }
`
